import type { AcceptInvitationResponse } from '~/types';

import { apiRequest } from './client';
import { ENDPOINTS, HTTP_METHODS } from './config';

/**
 * Invitation response matching backend InvitationResponse schema.
 * See: apps/api/src/models/invitation.py
 */
export interface InvitationResponse {
  id: string;
  organization_id: string;
  email: string;
  role: 'owner' | 'admin' | 'member';
  status: string;
  invited_at: string;
  expires_at: string;
  invited_by_user_id: string | null;
  token?: string; // Only included on creation/resend
  // Extended fields (populated when GET endpoint exists)
  organization_name?: string;
  inviter_name?: string;
  inviter_email?: string;
}

/**
 * Pending invitation item for user's invitation list.
 * See: apps/api/src/models/invitations.py::PendingInvitationItem
 */
export interface PendingInvitationItem {
  id: string;
  organization_id: string;
  organization_name: string;
  role: 'owner' | 'admin' | 'member';
  invited_at: string;
  invited_by_user_id: string | null;
  inviter_name?: string | null;
  inviter_email?: string | null;
  source: 'direct' | 'domain_governance';
  domain?: string | null;
}

/**
 * Request payload for sending an invitation.
 */
export interface SendInvitationRequest {
  email: string;
  role: 'admin' | 'member';
}

/**
 * Accept an invitation to join an organization using email token.
 * Token is sent in request body (not URL) for security.
 *
 * @param invitationId - Invitation ID
 * @param token - JWT invitation token
 * @returns Accept response with membership details
 * @throws ApiError with status 400 if token invalid/expired/mismatch
 * @throws ApiError with status 403 if email mismatch
 * @throws ApiError with status 404 if invitation not found
 * @throws ApiError with status 409 if already accepted
 * @throws ApiError with status 503 if authorization service unavailable
 */
export async function acceptInvitation(
  invitationId: string,
  token: string,
): Promise<AcceptInvitationResponse> {
  const response = await apiRequest<AcceptInvitationResponse>(
    `${ENDPOINTS.INVITATIONS}/${invitationId}/accept`,
    {
      method: HTTP_METHODS.POST,
      body: JSON.stringify({ token }),
    },
  );

  return response.data;
}

/**
 * Accept an invitation directly without email token (for local dev/authenticated users).
 *
 * This endpoint allows authenticated users to accept invitations directly
 * if their Firebase email matches the invitation email. Useful for:
 * - Local development without email infrastructure
 * - Streamlined UX when user is already authenticated
 *
 * NOTE: This bypasses email verification. Production may want to use token-based acceptance.
 *
 * @param invitationId - Invitation ID
 * @returns Accept response with membership details
 * @throws ApiError with status 403 if email doesn't match invitation
 * @throws ApiError with status 404 if invitation not found
 * @throws ApiError with status 409 if already accepted
 */
export async function acceptInvitationDirect(
  invitationId: string,
): Promise<AcceptInvitationResponse> {
  const response = await apiRequest<AcceptInvitationResponse>(
    `${ENDPOINTS.INVITATIONS}/${invitationId}/accept-direct`,
    {
      method: HTTP_METHODS.POST,
    },
  );

  return response.data;
}

/**
 * Decline/cancel an invitation.
 * Requires authentication (either as invitee or org admin).
 *
 * @param invitationId - Invitation ID
 * @throws ApiError with status 403 if not invitee or org admin
 * @throws ApiError with status 404 if not found or not pending
 */
export async function declineInvitation(invitationId: string): Promise<void> {
  await apiRequest(`${ENDPOINTS.INVITATIONS}/${invitationId}`, {
    method: HTTP_METHODS.DELETE,
  });
}

/**
 * Get invitation details by ID.
 * Note: Token is required for unauthenticated access.
 *
 * @param invitationId - Invitation ID
 * @param token - JWT invitation token
 * @returns Invitation details
 * @throws ApiError with status 404 if not found
 * @throws ApiError with status 401 if token expired/invalid
 */
export async function getInvitation(
  invitationId: string,
  token: string,
): Promise<InvitationResponse> {
  const params = new URLSearchParams();
  params.set('token', token);
  const response = await apiRequest<InvitationResponse>(
    `${ENDPOINTS.INVITATIONS}/${invitationId}?${params.toString()}`,
  );

  return response.data;
}

/**
 * Get pending invitations for the current authenticated user.
 * Shows all organizations the user has been invited to but hasn't accepted/declined yet.
 *
 * @returns List of pending invitations for the user
 */
export async function getMyInvitations(): Promise<PendingInvitationItem[]> {
  const response = await apiRequest<{ invitations: PendingInvitationItem[] }>(
    `${ENDPOINTS.USERS}/me/invitations`,
  );
  return response.data.invitations;
}

/**
 * Resend an existing invitation.
 *
 * @param invitationId - Invitation ID
 * @returns Updated invitation response
 * @throws ApiError with status 400 if invitation not in 'invited' status
 * @throws ApiError with status 403 if not admin/owner
 */
export async function resendInvitation(invitationId: string): Promise<InvitationResponse> {
  const response = await apiRequest<InvitationResponse>(
    `${ENDPOINTS.INVITATIONS}/${invitationId}/resend`,
    {
      method: HTTP_METHODS.POST,
    },
  );

  return response.data;
}

/**
 * Send an invitation to join an organization.
 *
 * @param orgId - Organization ID
 * @param data - Invitation data (email, role)
 * @returns Invitation response
 * @throws ApiError with status 409 if user already member or invitation pending
 * @throws ApiError with status 400 if invalid email
 * @throws ApiError with status 403 if not admin/owner
 */
export async function sendInvitation(
  orgId: string,
  data: SendInvitationRequest,
): Promise<InvitationResponse> {
  const response = await apiRequest<InvitationResponse>(
    `${ENDPOINTS.ORGANIZATIONS}/${orgId}/invitations`,
    {
      method: HTTP_METHODS.POST,
      body: JSON.stringify(data),
    },
  );

  return response.data;
}
